// src/components/mdx/client/visualizations/FeatureSignalCompositeChart.client.tsx

/**
 * @fileoverview Illustrates how a bundled ad masks an underlying creative feature signal.
 * @description Static Recharts line chart for MDX articles comparing stable feature contribution,
 * volatile single-ad outcomes, and a smoothed trailing read used to resist over-reaction.
 * @module src/components/mdx/client/visualizations/FeatureSignalCompositeChart.client
 */

"use client"

import { useMemo, type JSX } from "react"
import { useTheme } from "next-themes"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "~/lib/charts/client"
import { createDeterministicRandom, hashSeed } from "~/lib/utils/random"
import { calculateMovingAverage, type SignalNoiseDataPoint } from "~/components/mdx/client/visualizations/signalNoiseMath"
import { ChartVisibilityBoundary } from "~/components/mdx/client/visualizations/ChartVisibilityBoundary.client"
import { cn } from "~/lib/utils"

const DEFAULT_DAYS = 28
const MA_WINDOW = 5
const CHART_HEIGHT = 320

/**
 * Generates synthetic daily metrics: an underlying feature effect, a noisier bundled-ad read,
 * and a moving average that approximates how teams smooth volatile ad-level numbers.
 *
 * @param days - Number of sequential observations to simulate.
 * @returns {SignalNoiseDataPoint[]} Points with pureSignal as feature effect and noisySignal as bundled ad.
 */
function generateCompositeSeries(days: number): SignalNoiseDataPoint[] {
  const rnd = createDeterministicRandom(hashSeed("feature-signal-composite", "v1", days))
  const out: SignalNoiseDataPoint[] = []

  for (let i = 0; i < days; i += 1) {
    const t = i / Math.max(1, days - 1)
    const feature =
      0.62 +
      0.06 * Math.sin(t * Math.PI * 1.4) +
      0.015 * Math.sin(i * 0.55)
    const bundle = 0.12 * Math.sin(i * 0.9) + 0.08 * (rnd() - 0.5) + 0.06 * (rnd() - 0.5)
    const noisy = Math.min(0.98, Math.max(0.08, feature + bundle))

    out.push({
      x: i + 1,
      pureSignal: Math.round(feature * 1000) / 1000,
      noisySignal: Math.round(noisy * 1000) / 1000
    })
  }

  return out
}

export interface FeatureSignalCompositeChartProps {
  /** Number of days (data points) in the simulation. */
  days?: number
  /** Optional extra classes on the outer container. */
  className?: string
}

/**
 * Line chart comparing a stable creative-feature effect to noisy bundled-ad reads.
 *
 * @param props - Component properties.
 * @returns {JSX.Element} Chart with legend and tooltip.
 */
export function FeatureSignalCompositeChart({
  days = DEFAULT_DAYS,
  className
}: FeatureSignalCompositeChartProps): JSX.Element {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"

  const data = useMemo(() => {
    const base = generateCompositeSeries(days)
    const withMa = calculateMovingAverage(base, MA_WINDOW)
    return withMa.map((row, i) => ({
      day: row.x,
      feature: row.pureSignal,
      bundledAd: row.noisySignal,
      smoothedAd: row.movingAverage ?? base[i]?.noisySignal ?? row.noisySignal
    }))
  }, [days])

  return (
    <ChartVisibilityBoundary className={cn("my-8 w-full", className)}>
      {({ hasEntered }) =>
        hasEntered ? (
          <div
            className={cn(
              "rounded-xl border p-4 shadow-sm",
              "border-slate-200 bg-white",
              "dark:border-slate-700/80 dark:bg-slate-900/90 dark:shadow-lg dark:shadow-black/20 dark:ring-1 dark:ring-white/5"
            )}
          >
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              The bundled ad hides what repeats
            </h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Simulated daily index (0–1): teal shows a relatively stable underlying creative feature. Orange
              is a single bundled ad that includes that feature plus co-occurring variables. Purple is a short
              moving average of the ad read—helpful for dampening noise, not a substitute for feature tagging.
            </p>
            <div className="mt-4" style={{ height: CHART_HEIGHT }}>
              <ResponsiveContainer width="100%" height="100%" debounce={50}>
                <LineChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDark ? "#334155" : "#e2e8f0"}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="day"
                    stroke={isDark ? "#64748b" : "#94a3b8"}
                    tick={{ fill: isDark ? "#cbd5e1" : "#64748b", fontSize: 11 }}
                    label={{
                      value: "Day",
                      position: "insideBottom",
                      offset: -4,
                      fontSize: 11,
                      fill: isDark ? "#94a3b8" : "#64748b"
                    }}
                  />
                  <YAxis
                    domain={[0.35, 0.95]}
                    stroke={isDark ? "#64748b" : "#94a3b8"}
                    tick={{ fill: isDark ? "#cbd5e1" : "#64748b", fontSize: 11 }}
                    width={40}
                    label={{
                      value: "Index",
                      angle: -90,
                      position: "insideLeft",
                      style: {
                        textAnchor: "middle",
                        fontSize: 11,
                        fill: isDark ? "#94a3b8" : "#64748b"
                      }
                    }}
                  />
                  <Tooltip
                    formatter={(v: number) => v.toFixed(3)}
                    labelFormatter={(l: number) => `Day ${l}`}
                    contentStyle={
                      isDark
                        ? {
                            fontSize: 12,
                            backgroundColor: "#0f172a",
                            border: "1px solid #334155",
                            borderRadius: "0.5rem",
                            color: "#e2e8f0"
                          }
                        : {
                            fontSize: 12,
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "0.5rem",
                            color: "#0f172a"
                          }
                    }
                    labelStyle={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}
                    itemStyle={{ color: isDark ? "#e2e8f0" : "#334155" }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: 12,
                      color: isDark ? "#cbd5e1" : "#475569",
                      paddingTop: 8
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="feature"
                    name="Creative feature (stable-ish)"
                    stroke="#0d9488"
                    strokeWidth={2.25}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="bundledAd"
                    name="Single bundled ad outcome"
                    stroke="#ea580c"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="smoothedAd"
                    name={`${MA_WINDOW}-day MA of ad read`}
                    stroke="#7c3aed"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null
      }
    </ChartVisibilityBoundary>
  )
}

export default FeatureSignalCompositeChart
